import { es, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Checkbox, Input, InputLabel, ListItemText, MenuItem, Select, TextField, useMediaQuery } from '@mui/material'

import Dialog from './dialog'
import i18n from '../../utils/i18n.utils'
import { AppStateContext } from '../../contexts'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { Customer, FormError, SystemUser } from '../../types'
// import { parseNumber } from '../customer-companies-management/utils'
import AutocompleteWithSelectAllMixin from './autocomplete-with-select-all-mixin'
import { /* BalanceTransactionType, */ batchNoveltiesData, batchStatusesData, customersDocumentTypeData, systemUserRolesData, systemUserStatusesData /* transactionTypesData */ } from '../../lib'

export const FiltersOptionsModal = ({
  title,
  customersData,
  filtersToApply,
  systemUsersData,
  actionButtonText,
  showNoveltyTypeFilter: _showNoveltyTypeFilter,
  handleDialogOpenState
}: {
  title: string
  filtersToApply: string
  actionButtonText: string
  showNoveltyTypeFilter?: boolean
  customersData?: Array<Customer>
  systemUsersData?: Array<SystemUser>
  handleDialogOpenState: (openStateToSet: boolean) => void
}) => {
  const { banksData } = useContext(AppStateContext)

  const { t } = useTranslation(['reports-management', 'common'])
  const mdDown = useMediaQuery((theme: any) => theme.breakpoints.down('md'))

  const [errors, setErrors] = useState<FormError>({})
  // const [selectedAmount, setSelectedAmount] = useState('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [toDateToFilter, setToDateToFilter] = useState<Date | null>(null)
  const [fromDateToFilter, setFromDateToFilter] = useState<Date | null>(null)
  // const [selectedAccountNumberData, setSelectedAccountNumberData] = useState('')
  // const [selectedReferenceNumberData, setSelectedReferenceNumberData] = useState('')
  const [selectedBeneficiaryDocumentNumber, setSelectedBeneficiaryDocumentNumber] = useState('')
  // const [selectedBanks, setSelectedBanks] = useState<Array<{ value: string; label: string }>>([])
  const [customersList, setCustomersList] = useState<Array<{ value: string; label: string }>>([])
  const [, /* allBanksOptions */ setAllBanksOptions] = useState<Array<{ value: string; label: string }>>([])
  const [systemUsersList, setSystemUsersList] = useState<Array<{ value: string; label: string }>>([])
  const [selectedBatchStatus, setSelectedBatchStatus] = useState<Array<{ bdValue: string; name: string }>>([])
  const [selectedCustomers, setSelectedCustomers] = useState<Array<{ value?: string; label?: string } | null>>([])
  const [selectedBatchNovelties, setSelectedBatchNovelties] = useState<Array<{ bdValue: string; name: string }>>([])
  const [selectedSystemUserRolesType, setSelectedSystemUserRolesType] = useState<Array<{ value: string; name: string }>>([])
  // const [selectedNoveltiesType, setSelectedNoveltiesType] = useState<Array<{ value: BalanceTransactionType; name: string }>>([])
  const [selectedCustomerDocumentsType, setSelectedCustomerDocumentsType] = useState<Array<{ value: string; name: string }>>([])
  const [selectedSystemUserStatusesType, setSelectedSystemUserStatusesType] = useState<Array<{ value: string; name: string }>>([])
  const [selectedApprovedBySystemUsers, setSelectedApprovedBySystemUsers] = useState<Array<{ value?: string; label?: string } | null>>([])
  const [selectedUploadedBySystemUsers, setSelectedUploadedBySystemUsers] = useState<Array<{ value?: string; label?: string } | null>>([])

  useEffect(() => {
    if (systemUsersData) {
      const systemUsersListToSet = []

      for (const element of systemUsersData) {
        systemUsersListToSet.push({
          value: String(element._id),
          label: `${element.firstName.toUpperCase()} ${element.lastName.toUpperCase()}`.trim()
        })
      }

      const systemUsersListToSetData = []
      const seenLabels = new Set()

      for (const systemUserListItem of systemUsersListToSet) {
        if (!seenLabels.has(systemUserListItem.label)) {
          seenLabels.add(systemUserListItem.label)
          systemUsersListToSetData.push(systemUserListItem)
        }
      }

      setSystemUsersList(systemUsersListToSetData.sort((a, b) => a.label.localeCompare(b.label)))
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemUsersData])

  useEffect(() => {
    if (customersData) {
      const customersListToSet = []

      for (const element of customersData) {
        customersListToSet.push({
          value: String(element._id),
          label: String(element.registeredFullName).toUpperCase().trim()
        })
      }

      const customersListToSetData = []
      const seenLabels = new Set()

      for (const customerListItem of customersListToSet) {
        if (!seenLabels.has(customerListItem.label)) {
          seenLabels.add(customerListItem.label)
          customersListToSetData.push(customerListItem)
        }
      }

      setCustomersList(customersListToSetData.sort((a, b) => a.label.localeCompare(b.label)))
      setIsLoading(false)
    }
  }, [customersData])

  useEffect(() => {
    if (banksData) {
      const banksDataToDisplay = []

      for (let i = 0; i < banksData.length; i++) {
        const bankData = banksData[i]

        banksDataToDisplay.push({
          value: bankData.achCode,
          label: bankData.name.toUpperCase()
        })
      }

      setAllBanksOptions(banksDataToDisplay)
    }
  }, [banksData])

  /* const handleGetNumberAsCurrencyValue = (valueToSet: string) => {
    const extractedNumberOfValueToSet = valueToSet.replaceAll('$', '').replaceAll(',', '').replaceAll('.', '').trim()

    if (isNaN(Number(extractedNumberOfValueToSet))) return ''

    if (extractedNumberOfValueToSet === '') return ''

    return parseNumber(extractedNumberOfValueToSet)
  } */

  const handleVerifySelectedFilters = () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (!fromDateToFilter) {
      formDataIsValid = false
      formErrors.fromDateToFilter = t('modals.paymentsHistory.errors.fromDateToFilter')
    }

    if (!toDateToFilter) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilter')
    }

    if (fromDateToFilter && toDateToFilter && toDateToFilter?.getTime() < fromDateToFilter?.getTime()) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilterEarlier')
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }
  }

  return (
    <Dialog
      errors={errors}
      dialogTitle={title}
      dialogIsLoading={isLoading}
      actionButtonText={actionButtonText}
      minWidth={mdDown ? '0px' : '800px'}
      handleDialogOpenState={handleDialogOpenState}
      handleDialogAction={handleVerifySelectedFilters}
    >
      <div
        style={{ alignItems: 'flex-end', gap: '20px' }}
        className="payments-history-modal-full-column-container"
      >
        <div className="payments-history-modal-options-column">
          <div className="payments-history-modal-subtitle">
            {filtersToApply === 'payments'
              ? t('modals.paymentsHistory.subtitles.selectPaymentsCreationDateRange')
              : filtersToApply === 'beneficiaries'
              ? t('modals.paymentsHistory.subtitles.selectBeneficiariesCreationDateRange')
              : t('modals.paymentsHistory.subtitles.selectSettlementDateRange')}
          </div>
          <div>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
            >
              <DatePicker
                label={t('filters.fromCreationDate')}
                sx={{ width: '100%' }}
                onChange={(newValue) => setFromDateToFilter(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'standard',
                    error: !!errors.fromDateToFilter,
                    helperText: errors.fromDateToFilter,
                    sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                  }
                }}
              />
            </LocalizationProvider>
          </div>
        </div>
        <div className="payments-history-modal-options-column">
          <div>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
            >
              <DatePicker
                label={t('filters.toCreationDate')}
                sx={{ width: '100%' }}
                onChange={(newValue) => setToDateToFilter(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'standard',
                    error: !!errors.toDateToFilter,
                    helperText: errors.toDateToFilter,
                    sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                  }
                }}
              />
            </LocalizationProvider>
          </div>
        </div>
      </div>
      {filtersToApply === 'batches' && (
        <div
          style={{ marginTop: '20px' }}
          className="payments-history-modal-filters-container"
        >
          <div className="row-container">
            <div className="text-field-container">
              <div style={selectedBatchStatus.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                {selectedBatchStatus.length > 0 && <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('filters.batches.status')}</InputLabel>}
                <Select
                  multiple
                  fullWidth
                  value={selectedBatchStatus}
                  onChange={(event) => {
                    const selectedvalue = [...event.target.value] as Array<{ bdValue: string; name: string }>
                    setSelectedBatchStatus(selectedvalue)
                  }}
                  renderValue={(selected) => selected.map((x) => x.name).join(', ')}
                  input={
                    <Input
                      sx={{
                        ...textFieldWhiteBg,
                        '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                        '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                        '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                        '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                        '& .MuiSelect-select span::before': {
                          content: `"${t('filters.batches.status')}"`,
                          color: 'rgb(46, 211, 183)'
                        },
                        '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                      }}
                    />
                  }
                >
                  {batchStatusesData.map((batchStatusData: { bdValue: string; name: string }, index: number) => (
                    <MenuItem
                      key={index}
                      value={batchStatusData as unknown as string}
                    >
                      <Checkbox
                        sx={{
                          '& .MuiSvgIcon-root': { fill: '#15B79E' }
                        }}
                        checked={selectedBatchStatus.findIndex((item) => item.bdValue === batchStatusData.bdValue) >= 0}
                      />
                      <ListItemText primary={batchStatusData.name.toUpperCase()} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
            <div className="text-field-container">
              <div style={selectedBatchNovelties.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                {selectedBatchNovelties.length > 0 && <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('filters.batches.novelty')}</InputLabel>}
                <Select
                  multiple
                  fullWidth
                  value={selectedBatchNovelties}
                  onChange={(event) => {
                    const selectedvalue = [...event.target.value] as Array<{ bdValue: string; name: string }>
                    setSelectedBatchNovelties(selectedvalue)
                  }}
                  renderValue={(selected) => selected.map((x) => x.name).join(', ')}
                  input={
                    <Input
                      sx={{
                        ...textFieldWhiteBg,
                        '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                        '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                        '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                        '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                        '& .MuiSelect-select span::before': {
                          content: `"${t('filters.batches.novelty')}"`,
                          color: 'rgb(46, 211, 183)'
                        },
                        '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                      }}
                    />
                  }
                >
                  {batchNoveltiesData.map((batchNoveltyData: { bdValue: string; name: string }, index: number) => (
                    <MenuItem
                      key={index}
                      value={batchNoveltyData as unknown as string}
                    >
                      <Checkbox
                        sx={{
                          '& .MuiSvgIcon-root': { fill: '#15B79E' }
                        }}
                        checked={selectedBatchNovelties.findIndex((item) => item.bdValue === batchNoveltyData.bdValue) >= 0}
                      />
                      <ListItemText primary={batchNoveltyData.name.toUpperCase()} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={systemUsersList}
                setValue={setSelectedUploadedBySystemUsers}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedUploadedBySystemUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('filters.batches.uploadedBy')}
                    sx={{
                      ...textFieldWhiteBg,
                      '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                      '& .MuiAutocomplete-inputRoot': { paddingLeft: '20px' }
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={systemUsersList}
                setValue={setSelectedApprovedBySystemUsers}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedApprovedBySystemUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('filters.batches.approvedBy')}
                    sx={{
                      ...textFieldWhiteBg,
                      '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                      '& .MuiAutocomplete-inputRoot': { paddingLeft: '20px' }
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}
      {filtersToApply === 'payments' && (
        <div
          style={{ marginTop: '20px' }}
          className="payments-history-modal-filters-container"
        >
          <div className="row-container">
            <TextField
              select
              fullWidth
              variant="standard"
              label={t('filters.payments.status')}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              error={!!errors.role}
              helperText={errors.role}
              value=""
              onChange={(event) => console.log(event.target.value)}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('filters.payments.select')}
              </MenuItem>
              <MenuItem value="admin">{t('filters.payments.underReview')}</MenuItem>
              <MenuItem value="admin-builder">{t('filters.payments.readyToBeDisperse')}</MenuItem>
            </TextField>
          </div>
          {/* <div className="row-container">
            <div
              className="payments-history-modal-options-column"
              style={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.selectPaymentsPaidDateRange')}</div>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
              >
                <DatePicker
                  label={t('filters.fromDate')}
                  sx={{ width: '100%' }}
                  onChange={(newValue) => setFromDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                      error: !!errors.fromDateToFilter,
                      helperText: errors.fromDateToFilter,
                      sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
            <div
              style={{ justifyContent: 'flex-end' }}
              className="payments-history-modal-options-column"
            >
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
              >
                <DatePicker
                  label={t('filters.toDate')}
                  sx={{ width: '100%' }}
                  onChange={(newValue) => setToDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                      error: !!errors.toDateToFilter,
                      helperText: errors.toDateToFilter,
                      sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={customersList}
                setValue={setSelectedCustomers}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedCustomers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('filters.payments.beneficiary')}
                    sx={{
                      ...textFieldWhiteBg,
                      '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                      '& .MuiAutocomplete-inputRoot': { paddingLeft: '20px' }
                    }}
                  />
                )}
              />
            </div>
            {_showNoveltyTypeFilter && (
              <div className="text-field-container">
                <div style={selectedNoveltiesType.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedNoveltiesType.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.noveltyType')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedNoveltiesType}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: BalanceTransactionType; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedNoveltiesType(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedNoveltiesType(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: BalanceTransactionType; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedNoveltiesType(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: BalanceTransactionType; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedNoveltiesType(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span': {
                            position: 'relative',
                            bottom: '8px'
                          },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.noveltyType')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {transactionTypesData.map((noveltyTypeData: { value: BalanceTransactionType; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={noveltyTypeData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedNoveltiesType.findIndex((item) => item.value === noveltyTypeData.value) >= 0}
                        />
                        <ListItemText primary={noveltyTypeData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={allBanksOptions}
                setValue={setSelectedBanks}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedBanks}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('modals.paymentsHistory.options.bank')}
                    sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                  />
                )}
              />
            </div>
            <div className="text-field-container">
              <TextField
                fullWidth
                variant="standard"
                label={t('modals.paymentsHistory.options.accountNumber')}
                value={selectedAccountNumberData ? selectedAccountNumberData : ''}
                onChange={(event) => setSelectedAccountNumberData(event.target.value)}
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              />
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <TextField
                fullWidth
                variant="standard"
                label={t('modals.paymentsHistory.options.amount')}
                value={selectedAmount ? selectedAmount : ''}
                onChange={(event) => {
                  const amountToSet = handleGetNumberAsCurrencyValue(String(event.target.value))

                  if (amountToSet === '') {
                    setSelectedAmount(amountToSet)
                    return
                  }

                  setSelectedAmount(`$ ${amountToSet}`)
                }}
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              />
            </div>
            <div className="text-field-container">
              <TextField
                fullWidth
                variant="standard"
                label={t('modals.paymentsHistory.options.referenceNumber')}
                value={selectedReferenceNumberData ? selectedReferenceNumberData : ''}
                onChange={(event) => setSelectedReferenceNumberData(event.target.value)}
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              />
            </div>
          </div> */}
        </div>
      )}
      {filtersToApply === 'beneficiaries' && (
        <div
          style={{ marginTop: '20px' }}
          className="payments-history-modal-filters-container"
        >
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={customersList}
                setValue={setSelectedCustomers}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedCustomers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('filters.payments.beneficiary')}
                    sx={{
                      ...textFieldWhiteBg,
                      '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                      '& .MuiAutocomplete-inputRoot': { paddingLeft: '20px' }
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <TextField
                fullWidth
                variant="standard"
                label={t('modals.paymentsHistory.options.documentNumber')}
                onChange={(event) => setSelectedBeneficiaryDocumentNumber(event.target.value)}
                value={selectedBeneficiaryDocumentNumber ? selectedBeneficiaryDocumentNumber : ''}
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              />
            </div>
            <div className="text-field-container">
              <div className="text-field-container">
                <div style={selectedCustomerDocumentsType.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedCustomerDocumentsType.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.documentType')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedCustomerDocumentsType}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: string; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedCustomerDocumentsType(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedCustomerDocumentsType(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: string; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedCustomerDocumentsType(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: string; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedCustomerDocumentsType(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span': {
                            position: 'relative',
                            bottom: '8px'
                          },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.documentType')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {customersDocumentTypeData.map((customerDocumentTypeData: { value: string; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={customerDocumentTypeData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedCustomerDocumentsType.findIndex((item) => item.value === customerDocumentTypeData.value) >= 0}
                        />
                        <ListItemText primary={customerDocumentTypeData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {filtersToApply === 'systemUsers' && (
        <div
          style={{ marginTop: '20px' }}
          className="payments-history-modal-filters-container"
        >
          <div className="row-container">
            <div className="text-field-container">
              <AutocompleteWithSelectAllMixin
                id="autocomplete-with-select-all"
                multiple
                fullWidth
                options={systemUsersList}
                setValue={setSelectedUploadedBySystemUsers}
                disableCloseOnSelect
                filterSelectedOptions
                freeSolo={false}
                value={selectedUploadedBySystemUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('filters.systemUsers.name')}
                    sx={{
                      ...textFieldWhiteBg,
                      '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                      '& .MuiAutocomplete-inputRoot': { paddingLeft: '20px' }
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className="row-container">
            <div className="text-field-container">
              <div className="text-field-container">
                <div style={selectedSystemUserRolesType.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedSystemUserRolesType.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.systemUsersRoles')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedSystemUserRolesType}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: string; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedSystemUserRolesType(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedSystemUserRolesType(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: string; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedSystemUserRolesType(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: string; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedSystemUserRolesType(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span': {
                            position: 'relative',
                            bottom: '8px'
                          },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.systemUsersRoles')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {systemUserRolesData.map((systemUserRoleData: { value: string; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={systemUserRoleData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedSystemUserRolesType.findIndex((item) => item.value === systemUserRoleData.value) >= 0}
                        />
                        <ListItemText primary={systemUserRoleData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className="text-field-container">
              <div className="text-field-container">
                <div style={selectedSystemUserStatusesType.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedSystemUserStatusesType.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.systemUsersStatus')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedSystemUserStatusesType}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: string; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedSystemUserStatusesType(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedSystemUserStatusesType(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: string; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedSystemUserStatusesType(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: string; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedSystemUserStatusesType(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span': {
                            position: 'relative',
                            bottom: '8px'
                          },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.systemUsersStatus')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {systemUserStatusesData.map((systemUserStatusData: { value: string; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={systemUserStatusData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedSystemUserStatusesType.findIndex((item) => item.value === systemUserStatusData.value) >= 0}
                        />
                        <ListItemText primary={systemUserStatusData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}
